import * as THREE from 'three';
import { Entity } from '../../entities';
import {
  createMatteMaterial,
  createAtmosphereMaterial
} from '../../visual';
import { createPlanetSurface } from '../../visual/textures';
import type { Landable, LandablePalette } from './Landable';

export interface MoonOptions {
  readonly radius: number;
  readonly base: number;
  readonly accent: number;
  readonly detail: number;
  readonly halo: number;
  readonly clouds: number;
  readonly parent: Landable;
  readonly orbitDistance: number;
  readonly orbitSpeed?: number;
  readonly orbitTilt?: number;
  readonly initialAngle?: number;
  readonly spinSpeed?: number;
  readonly displayName?: string;
}

export class Moon extends Entity implements Landable {
  readonly radius: number;
  readonly displayName: string;
  readonly palette: LandablePalette;

  private readonly parent: Landable;
  private readonly orbitDistance: number;
  private readonly orbitSpeed: number;
  private readonly orbitTilt: number;
  private orbitAngle: number;
  private readonly spinSpeed: number;
  private readonly bodyMesh: THREE.Mesh;
  private readonly orbitGroup: THREE.Group;
  private readonly parentPos = new THREE.Vector3();

  constructor(options: MoonOptions) {
    super();
    this.radius = options.radius;
    this.displayName = options.displayName ?? 'Moon';
    this.palette = {
      base: options.base,
      accent: options.accent,
      detail: options.detail,
      halo: options.halo,
      clouds: options.clouds
    };
    this.parent = options.parent;
    this.orbitDistance = options.orbitDistance;
    this.orbitSpeed = options.orbitSpeed ?? 0.04;
    this.orbitTilt = options.orbitTilt ?? 0.3;
    this.orbitAngle = options.initialAngle ?? Math.random() * Math.PI * 2;
    this.spinSpeed = options.spinSpeed ?? 0.08;

    this.parent.getWorldPosition(this.parentPos);
    this.object3D.position.copy(this.parentPos);
    this.orbitGroup = new THREE.Group();
    this.orbitGroup.rotation.x = this.orbitTilt;
    this.object3D.add(this.orbitGroup);

    this.bodyMesh = this.buildBody(options);
    this.buildHalo();
    this.updateOrbit();
  }

  getWorldPosition(out: THREE.Vector3): THREE.Vector3 {
    return this.bodyMesh.getWorldPosition(out);
  }

  override update(dt: number): void {
    this.parent.getWorldPosition(this.parentPos);
    this.object3D.position.copy(this.parentPos);
    this.orbitAngle += this.orbitSpeed * dt;
    this.bodyMesh.rotation.y += this.spinSpeed * dt;
    this.updateOrbit();
  }

  private updateOrbit(): void {
    const angle = this.orbitAngle;
    this.bodyMesh.position.set(
      Math.cos(angle) * this.orbitDistance,
      0,
      Math.sin(angle) * this.orbitDistance
    );
  }

  private buildBody(options: MoonOptions): THREE.Mesh {
    const surface = createPlanetSurface({
      base: options.base,
      accent: options.accent,
      detail: options.detail
    });
    const material = createMatteMaterial({
      color: 0xffffff,
      roughness: 0.95,
      emissive: options.accent,
      emissiveIntensity: 0.03
    });
    material.map = surface;
    material.needsUpdate = true;

    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(options.radius, 40, 28),
      material
    );
    mesh.name = 'moon-body';
    this.orbitGroup.add(mesh);
    return mesh;
  }

  private buildHalo(): void {
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(this.radius * 1.08, 48, 32),
      createAtmosphereMaterial({
        color: this.palette.halo,
        intensity: 0.95,
        power: 3.2,
        sunBoost: 0.45
      })
    );
    halo.name = 'moon-halo';
    this.bodyMesh.add(halo);
  }
}
